import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { useCallback } from "react";
import { 
    FindAllCustomersOnboardingAdminQueryQuery, 
    QueryFindAllCustomersOnboardingAdminArgs
} from "../../../../../../schemaTypes";

export const findAllCustomersOnboardingAdminQuery = gql`
  query FindAllCustomersOnboardingAdminQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCustomersOnboardingAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        firstname
        lastname
        stage
        status
        email
        phone
        createdAt
        updatedAt
        referrer {
          userId
          id
          firstname
          lastname
        } 
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllCustomersOnboardingAdmin {
  data: FindAllCustomersOnboardingAdminQueryQuery["findAllCustomersOnboardingAdmin"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreCustomers: boolean;
}

interface Props {
  variables: QueryFindAllCustomersOnboardingAdminArgs;
  children: (data: WithFindAllCustomersOnboardingAdmin) => React.ReactElement | null;
}

export const FindAllCustomersOnboardingAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCustomersOnboardingAdminQueryQuery,
    QueryFindAllCustomersOnboardingAdminArgs
  >(findAllCustomersOnboardingAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllCustomersOnboardingAdminData = data?.findAllCustomersOnboardingAdmin?.data || [];
  const page = data?.findAllCustomersOnboardingAdmin?.page || variables.page;
  const pageSize = data?.findAllCustomersOnboardingAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllCustomersOnboardingAdmin?.total || 0;
  const totalPages = data?.findAllCustomersOnboardingAdmin?.totalPages || 0;
  const hasMoreCustomers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreCustomers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllCustomersOnboardingAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCustomersOnboardingAdmin: {
            ...fetchMoreResult?.findAllCustomersOnboardingAdmin,
            data: [
              ...(prev?.findAllCustomersOnboardingAdmin?.data || []),
              ...(fetchMoreResult?.findAllCustomersOnboardingAdmin?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreCustomers]);

  return children({
    data: findAllCustomersOnboardingAdminData as any,
    loading,
    loadMore,
    hasMoreCustomers,
    page,
    pageSize,
    total,
    totalPages,
  });
};